import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ArrowLeft,
  Trophy,
  Target,
  MessageSquare,
  Brain,
  Lightbulb,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ChevronRight,
  Loader2,
  UserCircle,
  Shield,
  Zap,
  Flame,
  Snowflake,
  Smile,
  Play,
  FileText,
  TrendingUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERSONA_ICONS = {
  budget_conscious: Snowflake,
  busy_decision_maker: Zap,
  technical_buyer: Shield,
  skeptical_buyer: Flame,
  friendly_noncommittal: Smile
};

const SCORE_CATEGORIES = [
  { key: 'discovery_quality', label: 'Discovery Quality', weight: '30%', icon: Target },
  { key: 'objection_handling', label: 'Objection Handling', weight: '25%', icon: Shield },
  { key: 'question_quality', label: 'Question Quality', weight: '15%', icon: MessageSquare },
  { key: 'conversation_control', label: 'Conversation Control', weight: '15%', icon: Zap },
  { key: 'closing_skill', label: 'Closing Skill', weight: '15%', icon: Trophy }
];

export default function RoleplayResults() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSession();
  }, [sessionId]);

  const fetchSession = async () => {
    try {
      const res = await fetch(`${API_URL}/api/roleplay/sessions/${sessionId}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSession(data);
      } else {
        navigate('/practice');
      }
    } catch (e) {
      navigate('/practice');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-600';
    if (score >= 60) return 'text-amber-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreBarColor = (score) => {
    if (score >= 80) return 'bg-emerald-500';
    if (score >= 60) return 'bg-yellow-500';
    if (score >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getGradeBg = (grade) => {
    if (!grade) return 'bg-secondary';
    if (grade.startsWith('A')) return 'bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border-emerald-500/40';
    if (grade.startsWith('B')) return 'bg-gradient-to-br from-blue-500/20 to-blue-600/10 border-blue-500/40';
    if (grade.startsWith('C')) return 'bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 border-yellow-500/40';
    return 'bg-gradient-to-br from-red-500/20 to-red-600/10 border-red-500/40';
  };

  const getGradeText = (grade) => {
    if (!grade) return 'text-muted-foreground';
    if (grade.startsWith('A')) return 'text-emerald-600';
    if (grade.startsWith('B')) return 'text-blue-600';
    if (grade.startsWith('C')) return 'text-amber-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!session) return null;

  const persona = session.persona || {};
  const scores = session.scores || {};
  const feedback = session.feedback || {};
  const transcript = session.transcript || [];
  const BuyerIcon = PERSONA_ICONS[session.persona_id] || UserCircle;

  return (
    <Layout>
      <div className="space-y-8" data-testid="roleplay-results">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate('/practice')}
              className="mb-2 -ml-2 text-muted-foreground hover:text-foreground"
              data-testid="back-to-practice"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Practice
            </Button>
            <h1 className="font-heading text-2xl font-bold text-foreground">Session Results</h1>
            <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <BuyerIcon className="w-4 h-4" />
                {persona.name}
              </span>
              <Badge className={`border text-xs ${session.difficulty === 'hard' ? 'bg-red-500/15 text-red-600 border-red-500/30' : session.difficulty === 'easy' ? 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30' : 'bg-yellow-500/15 text-amber-600 border-yellow-500/30'}`}>
                {session.difficulty}
              </Badge>
              <span>{transcript.length} messages</span>
            </div>
          </div>

          {/* Overall Score */}
          <div className={`flex items-center gap-4 p-5 rounded-2xl border ${getGradeBg(session.grade)}`}>
            <div className="text-center">
              <p className="text-xs text-muted-foreground mb-1">Overall Score</p>
              <p className={`font-heading text-5xl font-bold ${getScoreColor(session.overall_score || 0)}`} data-testid="overall-score">
                {session.overall_score || 0}
              </p>
            </div>
            <div className={`font-heading text-4xl font-bold ${getGradeText(session.grade)}`} data-testid="grade">
              {session.grade || '--'}
            </div>
          </div>
        </div>

        {/* Detailed Scores */}
        <div className="grid gap-4 md:grid-cols-5">
          {SCORE_CATEGORIES.map(({ key, label, weight, icon: Icon }) => {
            const score = scores[key] || 0;
            return (
              <Card key={key} className="bg-card border-border/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-4 h-4 text-primary" />
                    <span className="text-xs font-medium text-foreground">{label}</span>
                  </div>
                  <p className={`font-heading text-2xl font-bold ${getScoreColor(score)}`}>{score}</p>
                  <div className="mt-2 h-1.5 rounded-full bg-secondary overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${getScoreBarColor(score)}`} style={{ width: `${score}%` }} />
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">Weight: {weight}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Tabs for Feedback */}
        <Tabs defaultValue="coaching" className="w-full">
          <TabsList className="bg-card border border-border">
            <TabsTrigger value="coaching" className="data-[state=active]:bg-primary/10">
              <Brain className="w-4 h-4 mr-2" />
              Coaching
            </TabsTrigger>
            <TabsTrigger value="highlights" className="data-[state=active]:bg-primary/10">
              <Lightbulb className="w-4 h-4 mr-2" />
              Highlights
            </TabsTrigger>
            <TabsTrigger value="improve" className="data-[state=active]:bg-primary/10">
              <TrendingUp className="w-4 h-4 mr-2" />
              Next Steps
            </TabsTrigger>
            <TabsTrigger value="transcript" className="data-[state=active]:bg-primary/10">
              <FileText className="w-4 h-4 mr-2" />
              Transcript
            </TabsTrigger>
          </TabsList>

          {/* Coaching Tab */}
          <TabsContent value="coaching" className="mt-6 space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              {/* Strengths */}
              <Card className="bg-card border-emerald-500/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2 text-emerald-600">
                    <CheckCircle className="w-4 h-4" />
                    Strengths
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {(feedback.strengths || []).map((s, i) => (
                      <li key={`strength-${i}-${String(s).slice(0,20)}`} className="flex items-start gap-2 text-sm text-foreground">
                        <CheckCircle className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                        {s}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Weaknesses */}
              <Card className="bg-card border-red-500/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2 text-red-600">
                    <XCircle className="w-4 h-4" />
                    Weaknesses
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {(feedback.weaknesses || []).map((w, i) => (
                      <li key={`weakness-${i}-${String(w).slice(0,20)}`} className="flex items-start gap-2 text-sm text-foreground">
                        <XCircle className="w-3.5 h-3.5 text-red-600 mt-0.5 shrink-0" />
                        {w}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Missed Opportunities */}
            {feedback.missed_opportunities?.length > 0 && (
              <Card className="bg-card border-yellow-500/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2 text-amber-600">
                    <AlertTriangle className="w-4 h-4" />
                    Missed Opportunities
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {feedback.missed_opportunities.map((m, i) => (
                      <li key={`missed-${i}-${String(m).slice(0,20)}`} className="flex items-start gap-2 text-sm text-foreground">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
                        {m}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Coaching Tips */}
            {feedback.coaching_tips?.length > 0 && (
              <Card className="bg-card border-primary/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2 text-primary">
                    <Brain className="w-4 h-4" />
                    Coaching Tips
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {feedback.coaching_tips.map((t, i) => (
                      <li key={`coaching-${i}`} className="flex items-start gap-3 p-3 rounded-lg bg-primary/5 border border-primary/10">
                        <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                          <span className="text-xs font-bold text-primary">{i + 1}</span>
                        </div>
                        <span className="text-sm text-foreground">{t}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Highlights Tab */}
          <TabsContent value="highlights" className="mt-6">
            <Card className="bg-card border-border/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-amber-600" />
                  Conversation Highlights
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(feedback.conversation_highlights || []).length > 0 ? (
                  <div className="space-y-3">
                    {feedback.conversation_highlights.map((h, i) => (
                      <div key={`highlight-${i}`} className={`p-4 rounded-lg border ${
                        h.type === 'good' ? 'bg-emerald-500/5 border-emerald-500/20' :
                        h.type === 'bad' ? 'bg-red-500/5 border-red-500/20' :
                        'bg-yellow-500/5 border-yellow-500/20'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={`text-xs border ${
                            h.type === 'good' ? 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30' :
                            h.type === 'bad' ? 'bg-red-500/15 text-red-600 border-red-500/30' :
                            'bg-yellow-500/15 text-amber-600 border-yellow-500/30'
                          }`}>
                            {h.type === 'good' ? 'Good' : h.type === 'bad' ? 'Needs Work' : 'Missed'}
                          </Badge>
                        </div>
                        {h.rep_message && (
                          <p className="text-sm text-muted-foreground italic mb-2">"{h.rep_message}"</p>
                        )}
                        <p className="text-sm text-foreground">{h.feedback}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No highlights available</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Next Steps Tab */}
          <TabsContent value="improve" className="mt-6 space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <Card className="bg-card border-border/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    Skills to Improve
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {(feedback.skills_to_improve || []).map((s, i) => (
                      <li key={`skill-${i}-${String(s).slice(0,20)}`} className="flex items-start gap-2 text-sm text-foreground p-2 rounded-lg bg-secondary/30">
                        <ChevronRight className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                        {s}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-card border-border/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-primary" />
                    Questions to Practice
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {(feedback.recommended_questions || []).map((q, i) => (
                      <li key={`question-${i}-${String(q).slice(0,20)}`} className="flex items-start gap-2 text-sm text-foreground p-2 rounded-lg bg-secondary/30">
                        <span className="text-primary font-bold shrink-0">{i + 1}.</span>
                        {q}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>

            <Button onClick={() => navigate('/practice')} className="w-full h-12" data-testid="practice-again-btn">
              <Play className="w-5 h-5 mr-2" />
              Start Another Session
            </Button>
          </TabsContent>

          {/* Transcript Tab */}
          <TabsContent value="transcript" className="mt-6">
            <Card className="bg-card border-border/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Full Transcript
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px] rounded-lg border border-border p-4">
                  <div className="space-y-4">
                    {transcript.map((msg, idx) => (
                      <div key={`msg-${idx}-${msg.speaker || ''}`} className="flex gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                          msg.speaker === 'rep' ? 'bg-primary/20' : 'bg-secondary'
                        }`}>
                          {msg.speaker === 'rep' ? (
                            <UserCircle className="w-4 h-4 text-primary" />
                          ) : (
                            <BuyerIcon className="w-4 h-4 text-muted-foreground" />
                          )}
                        </div>
                        <div className="flex-1">
                          <span className={`text-xs font-medium ${
                            msg.speaker === 'rep' ? 'text-primary' : 'text-muted-foreground'
                          }`}>
                            {msg.speaker === 'rep' ? 'You (Sales Rep)' : persona.name}
                          </span>
                          <p className="text-sm text-foreground mt-0.5">{msg.message}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
